/**
 * Color Blind Filters
 * Implements SVG filter matrices for color blindness simulation and high contrast
 * Supports protanopia (red-blind), deuteranopia (green-blind), tritanopia (blue-blind)
 */

export type ColorBlindMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

// Color transformation matrices based on scientific color blindness simulation
// These matrices transform RGB values to simulate how colorblind individuals see colors
const COLOR_MATRICES: Record<ColorBlindMode, number[]> = {
  none: [
    1, 0, 0, 0, 0,
    0, 1, 0, 0, 0,
    0, 0, 1, 0, 0,
    0, 0, 0, 1, 0,
  ],
  // Protanopia (red-blind) - Difficulty distinguishing red from green
  protanopia: [
    0.567, 0.433, 0.000, 0, 0,
    0.558, 0.442, 0.000, 0, 0,
    0.000, 0.242, 0.758, 0, 0,
    0.000, 0.000, 0.000, 1, 0,
  ],
  // Deuteranopia (green-blind) - Most common, difficulty with red-green
  deuteranopia: [
    0.625, 0.375, 0.000, 0, 0,
    0.700, 0.300, 0.000, 0, 0,
    0.000, 0.300, 0.700, 0, 0,
    0.000, 0.000, 0.000, 1, 0,
  ],
  // Tritanopia (blue-blind) - Difficulty with blue-yellow
  tritanopia: [
    0.950, 0.050, 0.000, 0, 0,
    0.000, 0.433, 0.567, 0, 0,
    0.000, 0.475, 0.525, 0, 0,
    0.000, 0.000, 0.000, 1, 0,
  ],
};

// High contrast matrix (increases saturation and contrast)
const HIGH_CONTRAST_MATRIX = [
  1.5, -0.25, -0.25, 0, 0,
  -0.25, 1.5, -0.25, 0, 0,
  -0.25, -0.25, 1.5, 0, 0,
  0, 0, 0, 1, 0,
];

export class ColorBlindFilters {
  private mode: ColorBlindMode = 'none';
  private highContrast = false;
  private filterElement: SVGSVGElement | null = null;
  private styleElement: HTMLStyleElement | null = null;

  constructor() {
    this.createFilterElements();
  }

  private createFilterElements(): void {
    if (typeof document === 'undefined') return;

    // Create SVG element with filters
    this.filterElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.filterElement.setAttribute('id', 'accessibility-filters');
    this.filterElement.setAttribute('style', 'position: absolute; width: 0; height: 0; overflow: hidden;');
    this.filterElement.innerHTML = `
      <defs>
        <filter id="colorblind-filter" color-interpolation-filters="sRGB">
          <feColorMatrix type="matrix" values="${COLOR_MATRICES.none.join(' ')}" />
        </filter>
        <filter id="high-contrast-filter" color-interpolation-filters="sRGB">
          <feColorMatrix type="matrix" values="${HIGH_CONTRAST_MATRIX.join(' ')}" />
        </filter>
        <filter id="combined-filter" color-interpolation-filters="sRGB">
          <feColorMatrix id="colorblind-matrix" type="matrix" values="${COLOR_MATRICES.none.join(' ')}" result="colorblind" />
          <feColorMatrix type="matrix" values="${HIGH_CONTRAST_MATRIX.join(' ')}" in="colorblind" />
        </filter>
      </defs>
    `;

    // Create style element for applying filter
    this.styleElement = document.createElement('style');
    this.styleElement.id = 'accessibility-filter-styles';

    // Append to document
    document.body.appendChild(this.filterElement);
    document.head.appendChild(this.styleElement);
  }

  setMode(mode: ColorBlindMode): void {
    this.mode = mode;
    this.updateFilters();
  }

  setHighContrast(enabled: boolean): void {
    this.highContrast = enabled;
    this.updateFilters();
  }

  getMode(): ColorBlindMode {
    return this.mode;
  }

  isHighContrastEnabled(): boolean {
    return this.highContrast;
  }

  private updateFilters(): void {
    if (typeof document === 'undefined' || !this.filterElement || !this.styleElement) return;

    // Update the color matrix in the filter
    const matrix = COLOR_MATRICES[this.mode];
    const colorblindMatrix = this.filterElement.querySelector('#colorblind-matrix');
    const standaloneMatrix = this.filterElement.querySelector('#colorblind-filter feColorMatrix');

    if (colorblindMatrix) {
      colorblindMatrix.setAttribute('values', matrix.join(' '));
    }
    if (standaloneMatrix) {
      standaloneMatrix.setAttribute('values', matrix.join(' '));
    }

    // Determine which filter to apply
    let filterUrl = '';
    if (this.mode !== 'none' && this.highContrast) {
      filterUrl = 'url(#combined-filter)';
    } else if (this.mode !== 'none') {
      filterUrl = 'url(#colorblind-filter)';
    } else if (this.highContrast) {
      filterUrl = 'url(#high-contrast-filter)';
    }

    // Apply filter to game container
    if (filterUrl) {
      this.styleElement.textContent = `
        #game-container canvas {
          filter: ${filterUrl};
        }
      `;
    } else {
      this.styleElement.textContent = '';
    }
  }

  // Get adjusted color for a given hex color
  // Useful for UI elements that can't use CSS filters
  adjustColor(hexColor: number): number {
    if (this.mode === 'none' && !this.highContrast) {
      return hexColor;
    }

    // Extract RGB components
    const r = (hexColor >> 16) & 0xff;
    const g = (hexColor >> 8) & 0xff;
    const b = hexColor & 0xff;

    // Apply color matrix transformation
    let matrix = COLOR_MATRICES[this.mode];

    let newR = r * matrix[0] + g * matrix[1] + b * matrix[2];
    let newG = r * matrix[5] + g * matrix[6] + b * matrix[7];
    let newB = r * matrix[10] + g * matrix[11] + b * matrix[12];

    // Apply high contrast if enabled
    if (this.highContrast) {
      const hcm = HIGH_CONTRAST_MATRIX;
      const tempR = newR * hcm[0] + newG * hcm[1] + newB * hcm[2];
      const tempG = newR * hcm[5] + newG * hcm[6] + newB * hcm[7];
      const tempB = newR * hcm[10] + newG * hcm[11] + newB * hcm[12];
      newR = tempR;
      newG = tempG;
      newB = tempB;
    }

    // Clamp values
    newR = Math.max(0, Math.min(255, Math.round(newR)));
    newG = Math.max(0, Math.min(255, Math.round(newG)));
    newB = Math.max(0, Math.min(255, Math.round(newB)));

    return (newR << 16) | (newG << 8) | newB;
  }

  // Get a color palette adjusted for current settings
  getAdjustedPalette(palette: Record<string, number>): Record<string, number> {
    const adjusted: Record<string, number> = {};
    for (const [key, color] of Object.entries(palette)) {
      adjusted[key] = this.adjustColor(color);
    }
    return adjusted;
  }

  // Check if two colors would be distinguishable
  areColorsDistinguishable(color1: number, color2: number): boolean {
    const adjusted1 = this.adjustColor(color1);
    const adjusted2 = this.adjustColor(color2);

    // Calculate perceived luminance difference
    const lum1 = this.calculateLuminance(adjusted1);
    const lum2 = this.calculateLuminance(adjusted2);

    // WCAG recommends 4.5:1 ratio for normal text
    const ratio = (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
    return ratio >= 3; // Using 3:1 for graphical elements
  }

  private calculateLuminance(color: number): number {
    const r = ((color >> 16) & 0xff) / 255;
    const g = ((color >> 8) & 0xff) / 255;
    const b = (color & 0xff) / 255;

    const adjustChannel = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

    return 0.2126 * adjustChannel(r) + 0.7152 * adjustChannel(g) + 0.0722 * adjustChannel(b);
  }

  // Clean up DOM elements
  destroy(): void {
    if (this.filterElement && this.filterElement.parentNode) {
      this.filterElement.parentNode.removeChild(this.filterElement);
    }
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
    }
  }
}
